import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, ErrorState, StatusBadge, toneForStatus } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { useSoftDeleteTenantAdmin } from '../api/mutations.js';
import { useTenantAdmin } from '../api/queries.js';
import type { TenantAdminDetail } from '../api/schemas.js';

/**
 * One tenant administrator.
 *
 * Nothing here handles loading, errors, breadcrumbs or the correlation id —
 * `ResourceDetailPage` owns all four, so this file is only the fields this
 * resource has. That is the whole reason the shell is shared: a dozen detail
 * screens with a dozen copies of the state ordering is a dozen chances to
 * render "not found" while a request is still in flight.
 *
 * `DetailField` renders "Not recorded" for an empty value, so nothing here
 * needs a placeholder of its own.
 */

/** A UTC calendar date. The value is an ISO instant; the day is what reads. */
function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function AdminDetailGrid({ admin }: { readonly admin: TenantAdminDetail }): React.JSX.Element {
  return (
    <DetailGrid>
      <DetailField label="First name">{admin.firstName}</DetailField>
      <DetailField label="Last name">{admin.lastName}</DetailField>
      <DetailField label="Email">{admin.email}</DetailField>
      <DetailField label="Status">
        <StatusBadge tone={toneForStatus(admin.status)}>{admin.status}</StatusBadge>
      </DetailField>
      <DetailField label="Created">{formatDate(admin.createdAt)}</DetailField>
    </DetailGrid>
  );
}

export function TenantAdminDetailPage(): React.JSX.Element {
  const userId = useParams().userId ?? '';
  const [params] = useSearchParams();
  const tenantId = params.get('tenantId') ?? '';
  const navigate = useNavigate();
  const query = useTenantAdmin(userId, tenantId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const softDelete = useSoftDeleteTenantAdmin();

  if (userId === '') {
    return <ErrorState description="No administrator was requested." />;
  }

  if (tenantId === '') {
    return <ErrorState description="No tenant was selected for this administrator." />;
  }

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.TENANT_READ}
      parent={{ to: '/tenant-admins', label: 'Tenant administrators' }}
      title={(data: TenantAdminDetail) => data.displayName}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.TENANT_USER_MANAGE}>
            <Button asChild variant="outline">
              <Link to={`/tenant-admins/${userId}/edit?tenantId=${encodeURIComponent(tenantId)}`}>
                Edit
              </Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.TENANT_USER_MANAGE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete tenant administrator"
            description={`${data.displayName} will be removed from this tenant. Their audit trail and historical actions are retained.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              void (async () => {
                await softDelete.mutateAsync({ userId: data.id, tenantId: data.tenantId });
                setConfirmingDelete(false);
                void navigate(`/tenant-admins?tenantId=${encodeURIComponent(tenantId)}`);
              })();
            }}
          />
          <AdminDetailGrid admin={data} />
        </>
      )}
    </ResourceDetailPage>
  );
}
